import React, { useState, useEffect } from "react";
import { ANNEES_ANCRAGE } from "../constants.js";

// Convertit un objet {annee: nombre} en objet {annee: "texte"} pour l'édition
function toStringMap(obj, defaultVal) {
  return Object.fromEntries(
    ANNEES_ANCRAGE.map((a) => [a, String(obj?.[a] ?? defaultVal)])
  );
}

// Convertit un objet {annee: "texte"} en objet {annee: nombre} pour l'API
function toNumberMap(obj, defaultVal) {
  return Object.fromEntries(
    ANNEES_ANCRAGE.map((a) => {
      const n = Number(String(obj?.[a] ?? defaultVal).replace(",", "."));
      return [a, Number.isFinite(n) ? n : defaultVal];
    })
  );
}

export default function ParametresMargeModal({
  isOpen,
  onClose,
  fraisCultureHorsIrrigation,
  setFraisCultureHorsIrrigation,
  equipParams,
  setEquipParams,
  equipement, // sélectionné dans la sidebar pour déterminer colonne éditable
  lissage,
  setLissage,
  prixEau,
  setPrixEau,
  prixElec = {},
  setPrixElec,
}) {
  // brouillon local : les modifications ne s'appliquent qu'au clic sur
  // "Enregistrer" — fermer la popup (croix, clic en dehors) n'affecte rien.
  // Les prix restent en TEXTE pendant l'édition (pas en nombre), sinon taper
  // "0." se fait effacer avant même de pouvoir taper le chiffre suivant.
  const [draft, setDraft] = useState({
    fraisCultureHorsIrrigation,
    lissage,
    prixEau: toStringMap(prixEau, 0.1),
    prixElec: toStringMap(prixElec, 0.19),
    equipParams: equipParams || {},
  });

  // resynchronise le brouillon avec les vraies valeurs à chaque ouverture
  useEffect(() => {
    if (isOpen) {
      setDraft({
        fraisCultureHorsIrrigation,
        lissage,
        prixEau: toStringMap(prixEau, 0.1),
        prixElec: toStringMap(prixElec, 0.19),
        equipParams: equipParams || {},
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSave = () => {
    setFraisCultureHorsIrrigation(Number(draft.fraisCultureHorsIrrigation) || 0);
    setLissage(draft.lissage);
    setPrixEau(toNumberMap(draft.prixEau, 0.1));
    setPrixElec(toNumberMap(draft.prixElec, 0.19));
    // sauvegarde des paramètres par équipement
    const normalized = {};
    Object.keys(draft.equipParams || {}).forEach((eq) => {
      const e = draft.equipParams[eq] || {};
      normalized[eq] = {
        conso: Number(String(e.conso ?? 0).replace(",", ".")) || 0,
        investissement: Math.round(Number(e.investissement) || 0),
        duree_vie: Math.round(Number(e.duree_vie) || 0),
        entretien: Math.round((Number(String(e.entretien ?? 0).replace(",", ".")) || 0) * 10) / 10,
        efficite: Math.round((Number(String(e.efficite ?? 0).replace(",", ".")) || 0) * 10) / 10,
        main_doeuvre: Math.round(Number(e.main_doeuvre) || 0),
      };
    });
    setEquipParams(normalized);
    onClose();
  };

  return (
    <div className="diagoeau-modal-overlay" onClick={onClose}>
      <div className="diagoeau-modal" onClick={(e) => e.stopPropagation()}>
        <div className="diagoeau-modal-header">
          <h3>Affiner les paramètres</h3>
          <button className="diagoeau-modal-close" onClick={onClose} aria-label="Fermer">
            ×
          </button>
        </div>

        <div className="diagoeau-modal-body">
          <div className="diagoeau-field">
            <label>Frais de culture hors irrigation (€/ha/an)</label>
            <input
              type="number"
              step="0.01"
              value={draft.fraisCultureHorsIrrigation}
              onChange={(e) => setDraft({ ...draft, fraisCultureHorsIrrigation: e.target.value })}
            />
          </div>

          
          <div className="diagoeau-field">
            <label>Fenêtre de lissage (ans)</label>
            <input
              type="number"
              min={1}
              max={11}
              step={2}
              value={draft.lissage}
              onChange={(e) => setDraft({ ...draft, lissage: Number(e.target.value) })}
            />
          </div>

          <div className="diagoeau-field">
            <label>Coût de l'eau (€/m³) par période</label>
            <table className="diagoeau-data-table diagoeau-prix-eau-table">
              <thead>
                <tr>
                  <th>À partir de</th>
                  {ANNEES_ANCRAGE.map((a) => (
                    <th key={a}>{a}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Coût (€/m³)</td>
                  {ANNEES_ANCRAGE.map((a) => (
                    <td key={a}>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={draft.prixEau[a] ?? ""}
                        onChange={(e) =>
                          setDraft({ ...draft, prixEau: { ...draft.prixEau, [a]: e.target.value } })
                        }
                      />
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          <div className="diagoeau-field">
            <label>Coût de l'électricité (€/kWh) par période</label>
            <table className="diagoeau-data-table diagoeau-prix-eau-table">
              <thead>
                <tr>
                  <th>À partir de</th>
                  {ANNEES_ANCRAGE.map((a) => (
                    <th key={a}>{a}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Coût (€/kWh)</td>
                  {ANNEES_ANCRAGE.map((a) => (
                    <td key={a}>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={draft.prixElec[a] ?? ""}
                        onChange={(e) =>
                          setDraft({ ...draft, prixElec: { ...draft.prixElec, [a]: e.target.value } })
                        }
                      />
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          <div className="diagoeau-field">
            <label>Paramètres par équipement</label>
            <table className="diagoeau-data-table diagoeau-equip-table">
              <thead>
                <tr>
                  <th>Paramètre</th>
                  <th>Enrouleur</th>
                  <th>Pivot</th>
                  <th>Rampe</th>
                  <th>Goutte-à-goutte</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { key: "conso", label: "Consommation (kWh/m³)", step: "0.01" },
                  { key: "investissement", label: "Investissement (€/ha)", step: "1" },
                  { key: "duree_vie", label: "Durée de vie (ans)", step: "1" },
                  { key: "entretien", label: "Entretien (€/an)", step: "0.1" },
                  { key: "efficite", label: "Efficacité", step: "0.1" },
                  { key: "main_doeuvre", label: "Main d'oeuvre (€/ha/an)", step: "1" },
                ].map((param) => (
                  <tr key={param.key}>
                    <td>{param.label}</td>
                    {[["enrouleur", "pivot", "rampe", "gag"]].flat().map((eq) => {
                      const val = (draft.equipParams && draft.equipParams[eq] && draft.equipParams[eq][param.key]) ?? "";
                      const disabled = equipement !== eq;
                      return (
                        <td key={eq}>
                          <input
                            type="number"
                            step={param.step}
                            value={val}
                            disabled={disabled}
                            onChange={(e) =>
                              setDraft({
                                ...draft,
                                equipParams: {
                                  ...draft.equipParams,
                                  [eq]: { ...draft.equipParams[eq], [param.key]: e.target.value },
                                },
                              })
                            }
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="diagoeau-help-text">Les autres colonnes sont grisées — modifiez seulement l'équipement sélectionné dans la sidebar.</p>
          </div>
        </div>

          

        <div className="diagoeau-modal-footer">
          <button className="diagoeau-btn" onClick={handleSave}>
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}